


















import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import moment, { Moment } from "moment";
import _ from "lodash";
import SliderArrow from "@/shared/components/ui/SliderArrow.vue";
import PocGLineChart from "@/shared/components/charts/PocGLineChart.vue";
import { GradientPoint } from "@/shared/types";
import {
  tint,
  convertColorToHex,
  covertHexToRgbColor
} from "@/shared/colorAdjuster";
import { useThemeStore } from "@/shared/store/theme";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";

@Component({
  components: {
    SliderArrow,
    PocGLineChart
  }
})
export default class HsaLineChart extends Vue {
  dailyBalanceStore = useHsaDailyBalanceStore();
  themeStore = useThemeStore();
  featureFlagStore = useFeatureFlagStore();

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  @Prop()
  displayArrows: boolean;

  @Prop()
  containerClasses: string[];

  @Prop()
  height: number;

  @Prop()
  pocGraphHeight: number;

  @Prop({ required: true })
  initialDisplayMonths: number;

  @Prop()
  monthDisplaySpan: number;

  @Prop()
  displayPredictedData: boolean;

  @Prop()
  displayMonthNames: boolean;

  @Prop()
  displayGradientFill: boolean;

  get lightenedSecondaryColor (): string {
    const secondaryColorRgb = covertHexToRgbColor(this.themeStore.secondaryColor);
    const tintedColor = tint(secondaryColorRgb.r, secondaryColorRgb.g, secondaryColorRgb.b, 0.6);

    return convertColorToHex(tintedColor);
  }

  balancesLineChart = {
    classes: "balances-chart",
    width: 1064,
    height: this!.height,
    hasHovers: true
  };

  pocGLineChart = {
    width: "100%",
    height: this!.pocGraphHeight
  };

  positiveDataSetFillGradient: GradientPoint[] = [
    {
      alpha: 1.0,
      color: "#fff"
    },
    {
      alpha: 1.0,
      color: "#fff"
    }];

  selectedStartDate: Moment;
  selectedEndDate: Moment;

  mounted () {
    this.selectedStartDate = moment().subtract(this.initialDisplayMonths, "months");
    this.selectedEndDate = moment();

    this.dailyBalanceStore.setCurrentEndDateKey(this.selectedEndDate.format("YYYYMMDD"));
    this.loadDailyBalances();
    if (!this.username) {
      this.dailyBalanceStore.loadPredictedDailyBalances();
    }

    if (this.displayGradientFill) {
      this.positiveDataSetFillGradient = [
        {
          alpha: 1.0,
          color: this.lightenedSecondaryColor
        },
        {
          alpha: 1.0,
          color: this.lightenedSecondaryColor
        }];
    }
  }

  navigateForward (): void {
    if (this.navigateForwardPossible()) {
      this.selectedStartDate = this.selectedStartDate.add(this.monthDisplaySpan, "months");
      this.selectedEndDate = this.selectedEndDate.add(this.monthDisplaySpan, "months");
      this.dailyBalanceStore.setCurrentEndDateKey(this.selectedEndDate.format("YYYYMMDD"));
      this.loadDailyBalances();
    }
  }

  navigateBack (): void {
    if (this.navigateBackPossible()) {
      this.selectedEndDate = moment(this.selectedStartDate);
      this.selectedStartDate = moment(this.selectedEndDate).subtract(this.monthDisplaySpan, "months");
      this.dailyBalanceStore.setCurrentEndDateKey(this.selectedEndDate.format("YYYYMMDD"));
      this.loadDailyBalances();
    }
  }

  navigateBackPossible (): boolean {
    return moment(this.selectedStartDate) > moment(this.dailyBalanceStore.accountStartDate);
  }

  navigateForwardPossible (): boolean {
    return moment(this.selectedEndDate) < moment().subtract(1, "days");
  }

  incrementPredictedBalances (acc, curr) {
    const updatedCurr = {
      date: curr.date,
      employerAmount: curr.employerAmount,
      employeeRecurringAmount: curr.employeeRecurringAmount,
      employeeOneTimeAmount: curr.employeeOneTimeAmount,
      balance: curr.balance + acc.balance
    };

    return {
      items: [...acc.items, updatedCurr],
      balance: updatedCurr.balance
    };
  }

  get predictedBalances () {
    if (this.username && this.displayPredictedData) {
      // predicted balances are not allowed
      // HsaLineChart does not support predicted data for alternate user yet
      return null;
    }

    if (this.displayPredictedData && this.dailyBalanceStore.getterPredictedDailyBalances) {
      const lastRealDailyBalance: { balance: number, date: string } = _.last(
        this.dailyBalanceStore.balancesForCurrentDates);

      const today = moment();

      // do not show predicted balances when graph is showing past data
      if (this.selectedEndDate && this.selectedEndDate.isBefore(today)) {
        return null;
      }

      if (lastRealDailyBalance) {
        const filterDate = moment(lastRealDailyBalance.date).add(3, "months");
        const realBalance = lastRealDailyBalance.balance;
        const filteredPredictedBalances = this.dailyBalanceStore.getterPredictedDailyBalances.filter(
          item => moment(item.date) < filterDate);

        const results = filteredPredictedBalances.reduce(this.incrementPredictedBalances,
          { items: [], balance: realBalance });

        return results.items;
      }

      const filterDate = moment().add(3, "months");
      const filteredPredictedBalances = this.dailyBalanceStore.getterPredictedDailyBalances.filter(
        item => moment(item.date) < filterDate);
      const results = filteredPredictedBalances.reduce(this.incrementPredictedBalances, { items: [], balance: 0 });

      return results.items;
    }
    return null;
  }

  loadDailyBalances () {
    const startDate = this.selectedStartDate.format("YYYY-MM-DD");
    const endDate = this.selectedEndDate.add(1, "day").format("YYYY-MM-DD");

    if (this.username) {
      this.dailyBalanceStore.loadDailyBalances({
        username: this.username,
        partnerId: this.partnerId,
        startDate,
        endDate
      });
    } else {
      this.dailyBalanceStore.loadDailyBalances({
        startDate,
        endDate
      });
    }
  }

  @Watch("username")
  @Watch("partnerId")
  reloadDailyBalancesChart () {
    this.loadDailyBalances();
  }
}
